import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseAsString, useQueryStates } from 'nuqs';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { SUBSCRIPTION_STATUSES, SUBSCRIPTION_STATUS_LABEL, type PlatformPage, type PlatformSubscriptionRow } from '@barbeiropro/contracts';
import { CircleX, RotateCcw } from 'lucide-react';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { useTableParams } from '@/components/shared/use-table-params';
import { api, errorMessage } from '@/lib/api';
import { money } from '@/lib/format';
import { usePlans } from './orgs-page';
import { dd, FilterSelect, IconAction, INTERVAL_LABEL, SubPill, useList, usePlatformAbility } from './shared';

export function SubscriptionsPage() {
  const params = useTableParams({ sort: 'organizationName' });
  const [f, setF] = useQueryStates({ status: parseAsString, planId: parseAsString }, { history: 'replace' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const plans = usePlans();
  const { canBilling } = usePlatformAbility();
  const query = { ...params.query, status: f.status ?? undefined, planId: f.planId ?? undefined };
  const list = useList<PlatformPage<PlatformSubscriptionRow>>('subscriptions', '/platform/subscriptions', query);
  const act = useMutation({
    mutationFn: ({ orgId, action, body }: { orgId: string; action: string; body: unknown }) => api.post(`/platform/subscriptions/${orgId}/${action}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast.success('Assinatura atualizada');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const columns: DataColumn<PlatformSubscriptionRow>[] = [
    { id: 'organizationName', header: 'Barbearia', meta: { sortKey: 'organizationName' }, cell: ({ row }) => <span className="font-medium">{row.original.organizationName}</span> },
    { id: 'planName', header: 'Plano', meta: { sortKey: 'planName' }, cell: ({ row }) => `${row.original.planName} · ${money(row.original.priceCents)}/${INTERVAL_LABEL[row.original.interval]}` },
    { id: 'status', header: 'Situação', meta: { sortKey: 'status' }, cell: ({ row }) => (<span className="flex flex-wrap gap-1"><SubPill status={row.original.status} />{row.original.cancelAtPeriodEnd && <span className="text-xs text-muted-foreground">cancela no fim do período</span>}</span>) },
    { id: 'trial', header: 'Teste até', cell: ({ row }) => dd(row.original.trialEndsAt) },
    { id: 'currentPeriodEnd', header: 'Próxima cobrança', meta: { sortKey: 'currentPeriodEnd' }, cell: ({ row }) => dd(row.original.currentPeriodEnd) },
    { id: 'grace', header: 'Carência até', cell: ({ row }) => dd(row.original.graceUntil) },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      cell: ({ row }) => {
        const r = row.original;
        if (!canBilling) return null;
        return (
          <span onClick={(e) => e.stopPropagation()}>
            {r.status === 'canceled' || r.cancelAtPeriodEnd ? (
              <ConfirmDialog destructive={false} trigger={<IconAction label={`Reativar assinatura de ${r.organizationName}`} icon={<RotateCcw className="size-4" aria-hidden />} variant="outline" />} title="Reativar assinatura?" description={`A assinatura de ${r.organizationName} volta a ficar ativa.`} confirmLabel="Reativar" onConfirm={() => act.mutateAsync({ orgId: r.organizationId, action: 'reactivate', body: {} })} />
            ) : (
              <ConfirmDialog trigger={<IconAction label={`Cancelar assinatura de ${r.organizationName}`} icon={<CircleX className="size-4" aria-hidden />} variant="outline" />} title="Cancelar assinatura?" description={`A assinatura de ${r.organizationName} será cancelada no fim do período atual. Os dados são mantidos.`} confirmLabel="Cancelar assinatura" onConfirm={() => act.mutateAsync({ orgId: r.organizationId, action: 'cancel', body: { immediate: false } })} />
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader title="Assinaturas" description="Situação de cobrança de cada barbearia. O estado só muda por eventos confirmados do provedor ou por ação de um administrador." />
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={list.refetch}
        searchPlaceholder="Buscar por barbearia"
        onRowClick={(r) => navigate(`/plataforma/organizacoes/${r.organizationId}`)}
        empty={{ title: 'Nenhuma assinatura encontrada' }}
        filters={
          <>
            <FilterSelect label="Situação" value={f.status} onChange={(v) => { setF({ status: v }); params.set({ page: 1 }); }} options={SUBSCRIPTION_STATUSES.map((s) => ({ value: s, label: SUBSCRIPTION_STATUS_LABEL[s] }))} />
            <FilterSelect label="Plano" value={f.planId} onChange={(v) => { setF({ planId: v }); params.set({ page: 1 }); }} options={(plans.data ?? []).map((p) => ({ value: p.id, label: p.name }))} />
          </>
        }
      />
    </div>
  );
}
